import React, { useMemo, useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import PeopleTableSkeleton from "../../../../components/dashboardcomponents/People/PeopleTableSkeleton";
import PeopleTable from "../../../../components/dashboardcomponents/People/PeopleTable";
import StatusChip from "../../../../components/dashboardcomponents/People/StatusChip";
import GroupFormModal from "../../../../components/dashboardcomponents/People/Groups/GroupFormModal";
import GroupArchiveModal from "../../../../components/dashboardcomponents/People/Groups/GroupArchiveModal";
import GroupActivateModal from "../../../../components/dashboardcomponents/People/Groups/GroupActivateModal";
import { GROUP_OPEN_CREATE_MODAL } from "../../../../components/dashboardcomponents/People/Groups/groupEvents";
import { filterGroups } from "../../../../utils/peopleHelpers";
import { peopleTableEllipsisSx } from "../../../../components/dashboardcomponents/People/peopleFormValidation";
import {
  activateGroup,
  archiveGroup,
  createGroup,
  fetchAccessibleGroups,
  updateGroup,
} from "../../../../store/features/groupsApi";
import { buildGroupWritePayload } from "../../../../utils/groupMappers";
import usePeoplePermissions from "../../../../hooks/usePeoplePermissions";
import "../../TicketManagment/style.css";

function apiMessage(payload, fallback) {
  return payload?.message || fallback;
}

export default function Groups() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "all";
  const search = searchParams.get("q") || "";

  const { list: rows, listLoading, mutationLoading } = useSelector((state) => state.peopleGroups);
  const perms = usePeoplePermissions();
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [activateTarget, setActivateTarget] = useState(null);
  const rowsPerPage = 10;

  const reloadList = useCallback(
    () => dispatch(fetchAccessibleGroups()),
    [dispatch]
  );

  useEffect(() => {
    reloadList();
  }, [reloadList]);

  useEffect(() => {
    setPage(0);
  }, [tab, search]);

  useEffect(() => {
    const openCreate = () => {
      if (perms.groups.canCreate) setCreateOpen(true);
    };
    window.addEventListener(GROUP_OPEN_CREATE_MODAL, openCreate);
    return () => window.removeEventListener(GROUP_OPEN_CREATE_MODAL, openCreate);
  }, [perms.groups.canCreate]);

  const filteredRows = useMemo(
    () => filterGroups(rows, { tab, search }),
    [rows, tab, search]
  );

  const columns = [
    {
      key: "name",
      label: "Group Name",
      width: "minmax(180px, 1.4fr)",
      render: (row) => (
        <Typography sx={{ color: "#061522", fontSize: 14, fontWeight: 500, ...peopleTableEllipsisSx }}>
          {row.name}
        </Typography>
      ),
    },
    { key: "totalTeams", label: "Total Teams", width: "120px" },
    { key: "totalAgents", label: "Total Agents", width: "120px" },
    {
      key: "totalChats",
      label: "Total Chats",
      width: "120px",
      render: (row) => 
        <Typography sx={{ color: "#405261", fontSize: 14, fontWeight: 400 }}>
          {(row.totalChats ?? 0).toLocaleString()}
        </Typography>
    },
    {
      key: "totalTickets",
      label: "Total Tickets",
      width: "120px",
      render: (row) => <Typography sx={{ color: "#405261", fontSize: 14, fontWeight: 400 }}>
        {(row.totalTickets ?? 0).toLocaleString()}
      </Typography>
    },
    {
      key: "status",
      label: "Status",
      width: "minmax(100px, 0.45fr)",
      render: (row) => <StatusChip status={row.status} />,
    },
    { key: "createdDate", label: "Created Date", width: "160px" },
  ];

  const actionMenu = useMemo(
    () => [
      { key: "view", label: "View Detail" },
      { key: "analytics", label: "View Analytics" },
      {
        key: "archive",
        label: "Archive Group",
        hidden: (row) => !perms.groups.canArchive || row?.status === "archived",
      },
      {
        key: "activate",
        label: "Activate Group",
        hidden: (row) => !perms.groups.canActivate || row?.status !== "archived",
      },
    ],
    [perms]
  );

  const onAction = (type, row) => {
    if (type === "archive") {
      setArchiveTarget(row);
    } else if (type === "activate") {
      setActivateTarget(row);
    } else if (type === "view") {
      navigate(`/people/groups/${row.id}`, { state: { group: row } });
    } else if (type === "analytics") {
      toast.info(`View Analytics · ${row.name}`);
    }
  };

  const handleRowClick = (row) => {
    navigate(`/people/groups/${row.id}`, { state: { group: row } });
  };

  const handleCreate = async (form) => {
    const body = buildGroupWritePayload(form);
    const result = await dispatch(createGroup(body));
    if (createGroup.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, "Group created"));
      setCreateOpen(false);
      reloadList();
    } else {
      toast.error(apiMessage(result.payload, "Failed to create group"));
    }
  };

  const handleEdit = async (form) => {
    if (!editTarget) return;
    const result = await dispatch(
      updateGroup({
        groupId: editTarget.id,
        body: buildGroupWritePayload(form),
      })
    );
    if (updateGroup.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, "Group updated"));
      setEditTarget(null);
      reloadList();
    } else {
      toast.error(apiMessage(result.payload, "Failed to update group"));
    }
  };

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) return;
    const result = await dispatch(archiveGroup({ groupId: archiveTarget.id }));
    if (archiveGroup.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, `${archiveTarget.name} archived`));
      setArchiveTarget(null);
      reloadList();
    } else {
      toast.error(apiMessage(result.payload, "Failed to archive group"));
    }
  };

  const handleActivateConfirm = async () => {
    if (!activateTarget) return;
    const result = await dispatch(activateGroup({ groupId: activateTarget.id }));
    if (activateGroup.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, `${activateTarget.name} activated`));
      setActivateTarget(null);
      reloadList();
    } else {
      toast.error(apiMessage(result.payload, "Failed to activate group"));
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        className="Ticket-container-render"
        sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, width: "100%" }}
      >
        {listLoading ? (
          <PeopleTableSkeleton columns={columns} />
        ) : (
          <PeopleTable
            columns={columns}
            rows={filteredRows}
            page={page}
            rowsPerPage={rowsPerPage}
            onChangePage={(p) => setPage(p)}
            actionMenu={actionMenu}
            onAction={onAction}
            onRowClick={handleRowClick}
            entityLabel="Groups"
            selectable={false}
            horizontalBleed={24}
          />
        )}
      </Box>

      <GroupFormModal
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
        onConfirm={handleCreate}
        submitting={mutationLoading}
      />

      <GroupFormModal
        open={Boolean(editTarget)}
        mode="edit"
        initialValues={editTarget ? { name: editTarget.name, status: editTarget.status } : null}
        onClose={() => setEditTarget(null)}
        onConfirm={handleEdit}
        submitting={mutationLoading}
      />

      <GroupArchiveModal
        open={Boolean(archiveTarget)}
        groupName={archiveTarget?.name}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchiveConfirm}
      />

      <GroupActivateModal
        open={Boolean(activateTarget)}
        group={activateTarget}
        onClose={() => setActivateTarget(null)}
        onConfirm={handleActivateConfirm}
      />
    </Box>
  );
}
